import angular from 'angular';
import {asyncDigest} from '@/angular/libs/angular-await';
import {localeEvents} from "@/angular/modules/vrm.locales";
import {isBeta} from '@/utils/beta';


angular.module('vrm.core')
    .controller('LoginController', LoginController);

/**
 * @constructor handle login page
 * @ngInject
 */
function LoginController(
    InstallationsService,
    UserGroupsService,
    InstallationGroupsService,
    $scope,
    $rootScope,
    LoginService,
    $state,
    UserService,
    $stateParams,
    ApiService,
    VrmModalService,
    vrmNotificationsService,
    vrmNotificationType,
    vrmUserAccessLevel,
    locale,
    jwtHelper,
    $location,
    localStorageService,
    $q,
    $httpParamSerializer,
    VrmLangService,
    VrmNavService,
    vrmConfig,
) {
    // Hook up model
    $scope.loginModel = LoginService.loginModel;

    $scope.loggedIn = ApiService.isLoggedIn();
    $scope.user = null;
    $scope.redirecting = false;

    $scope.$watch(function() {
        return UserService.user;
    }, function(user) {
        $scope.user = user;
    });

    $scope.isBeta = isBeta();

    function redirect() {
        const singleInstallationId = InstallationsService.getSingleInstallationId();
        if (singleInstallationId) {
            redirectToSite(singleInstallationId);
            return;
        }

        if ($stateParams.returnUrl === "base.installation-overview") {
            // On first visit to installation overview, force open menu, so the user
            // can see the installations list in the menu before the installation overview loads
            VrmNavService.setForceOpenMenu(true);

            // Any login requests with these parameters shouldn't redirect to fleet-monitor
            // Otherwise side-effects happen, such as:
            // - /verifyinvite request is cancelled
            // - /add-installation user lands on fleet-monitor
            // - etc.
            const overrideFleetMonitorRedirect = Boolean(
                $stateParams.installationIdentifier ||
                $stateParams.device ||
                $stateParams.productId ||
                $stateParams.invite ||
                $stateParams.token ||
                $stateParams.evt
            );

            const siteCount = UserService.getSiteCountFromLocalStorage();
            const threshold = vrmConfig.fleetOwnerThreshold || 10;
            if (!overrideFleetMonitorRedirect && siteCount && siteCount >= threshold) {
                // The site count already exists in local storage from a previous session. Use it to redirect.
                $state.go('base.fleet-monitor', mfaCheckAfterRedirect, {location: 'replace'});
                return;
            }

            // Redirect to installation overview and pass the justSignedIn param so the page knows the user has just signed in.
            // If just signed in, the installation-overview page will check the installation count and redirect to /fleet-monitor
            // if needed.
            const stateParams = {
                ...$stateParams.returnParams,
                justSignedIn: true,
                overrideFleetMonitorRedirect: overrideFleetMonitorRedirect
            };

            $state.go('base.installation-overview', stateParams, {location: 'replace'}).then(mfaCheckAfterRedirect, function () {
                $state.go('base.installation-overview', mfaCheckAfterRedirect, {
                    location: 'replace'
                });
            });
        } else {
            // Try to redirect to returnUrl, if that fails go to installation overview, failure may occur if we do not have correct routing params
            $state.go($stateParams.returnUrl, $stateParams.returnParams, {location: 'replace'}).then(mfaCheckAfterRedirect, function () {
                $state.go('base.installation-overview', mfaCheckAfterRedirect, {
                    location: 'replace'
                });
            });
        }
    }

    function redirectToSite(idSite) {
        // Try to redirect to site dashboard, if that fails go to installation overview, failure may occur if the installation no longer exists or an error occurs
        $state.go('base.installation.dashboard', { id: idSite }).then(mfaCheckAfterRedirect, function () {
            $state.go('base.installation-overview', mfaCheckAfterRedirect, {
                location: 'replace'
            });
        });
    }

    function redirectToSiteByInstallationIdentifier(installationIdentifier, device, productId) {
        UserService
            .getUser()
            .then(function(user){
                ApiService
                    .call('/users/' + user.id +'/get-site-id?installation_identifier=' + installationIdentifier, null, 'GET', null, null, true)
                    .then(function (result) {
                        if (result.data.success) {
                            redirectToSite(result.data.records.site_id);
                        } else {
                            // If the identifier is unknown redirect to the add installation page and prefill it.
                            if (result.data.error_code === 'site_identifier_unknown' || result.data.error_code === 'site_unassigned' || result.data.error_code === 'access_denied') {
                                const prefillData = { installationIdentifier: installationIdentifier, device: device, productId: productId};
                                $state.go('base.add-installation', prefillData, {location: 'replace'} ).then(function () { }, function () {
                                    $state.go('base.add-installation', null, {location: 'replace'});
                                });
                            } else {
                                redirect();
                            }
                        }
                    })
                    .catch(redirect);
            });
    }

    function mfaCheckAfterRedirect() {
        UserService.getUser().then(function (user) {
            if (user.accessLevel !== vrmUserAccessLevel.SET_UP_MFA){
                return
            }

            VrmModalService.open2FAMandatoryModal();
        });
    }

    function makeInviteVerifyRequest(invite_key, invite_type) {
        // Wait for verifyinvite to complete, before continuing with redirects etc.
        // Important to do, otherwise, when redirecting to fleet-monitor page the request is cancelled
        return ApiService.call('/auth/verifyinvite', { invite_key, invite_type }, 'POST')
            .then(function(response) {
                // Schedule installation service reload to get the new site/groups/teams
                InstallationsService.reload();
                return response;
            });
    }

    function acceptSiteInvite(invite) {
        return makeInviteVerifyRequest(invite, 'site').then(function (inviteValidationResult) {
            vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('login.invite_accepted') });
            redirectToSite(inviteValidationResult.data.data.idSite);
            $scope.loginModel.username = "";
            $scope.loginModel.password = "";
            $scope.loginModel.sms_token = null;
            $scope.loginModel.language = "";
        }).catch(redirect);
    }

    function acceptUserGroupInvite(invite) {
        return makeInviteVerifyRequest(invite, 'user-group').then(() => {
            vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('login.team_invite_accepted') });
            UserGroupsService.reload();
        }).catch(redirect);
    }

    function acceptSiteGroupInvite(invite) {
        return makeInviteVerifyRequest(invite, 'installation-group').then(() => {
            vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('login.group_invite_accepted') });
            InstallationGroupsService.reload();
        }).catch(redirect);
    }

    function acceptInvite() {
        const inviteKey = $stateParams.invite;
        const inviteType = $stateParams.type;
        switch (inviteType) {
            default:
            case 'site': return acceptSiteInvite(inviteKey);
            case 'user-group': return acceptUserGroupInvite(inviteKey);
            case 'installation-group': return acceptSiteGroupInvite(inviteKey);
        }
    }

    function goToVictronConnect(data) {
        $scope.redirecting = true;
        UserService.getUser().then(function(user) {
            data.email = user.email;
            document.location = 'victron-connect://vrm-login?'+$httpParamSerializer(data);
        });
    }

    function getTemporaryToken() {
        const promise = $q.defer();
        ApiService.call('/auth/generatetoken').then(function(data) {
            promise.resolve(data.data);
        }).catch(function() {
            vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: locale.getString('login.token_error') }, true);
            promise.reject()
        });
        return promise.promise;
    }

    $scope.goToVictronConnect = function() {
        getTemporaryToken().then(goToVictronConnect);
    };

    $scope.demo = function () {
        // call api
        return ApiService
            .call("/auth/loginAsDemo", null, 'POST', true)
            .then(function (response) {
                // Token property included in response
                if (angular.isDefined(response.data.token)) {
                    if (_.isString(response.data.token) && response.data.token.length > 0) {
                        // store token
                        localStorageService.set("token", response.data.token);
                        // notify success
                        $scope.$emit('login');
                        $state.go('base.installation-overview');
                    }

                }
            }, function () {
                    return false;
            });
    };

    //Language setting runs after ApiService is called
    $scope.openLanguageModal = VrmModalService.openLanguageModal;
    VrmLangService.getLang().then(function(lang) {
        $scope.lang = lang;
    });
    $scope.$on(localeEvents.localeChanges, function (event, lang) {
        $scope.lang = lang;
        $scope.loginModel.language = lang;
    });

    function resetValidation() {
        if ($scope.loginForm) {
            $scope.loginForm.$setPristine();
            $scope.loginForm.$setUntouched();
        }
    }

    function onLoginSuccess() {
        $scope.$emit('login');
        if ($stateParams.invite) {
            acceptInvite();
        } else {
            if ($stateParams.victronConnect) {
                $scope.goToVictronConnect();
            } else if ($stateParams.installationIdentifier) {
                redirectToSiteByInstallationIdentifier($stateParams.installationIdentifier, $stateParams.device, $stateParams.productId);
            } else {
                redirect();
            }

            resetValidation();
            $scope.loginModel.username = '';
            $scope.loginModel.password = '';
            $scope.loginModel.sms_token = null;
            $scope.loginModel.language = '';

            $scope.mfaRequired = false;
            $scope.mfaMode = 'password';
            $scope.mfaFailure = false;
        }
    }

    $scope.mfaRequired = false;
    $scope.mfaMode = 'password';

    // Tries to log in user automatically once sms token is filled in
    $scope.checkMfaLength = function () {
        if ($scope.loginModel.sms_token.length === 6) {
            $scope.doLogin();
        }
    };

    // Hook up login action
    $scope.doLogin = asyncDigest($scope, async () => {
        // Logout an login for the cases when the user is already logged in
        // Clears contexts and user specific data.
        $scope.$emit('logout');

        // only continue
        if ($scope.loginForm && !$scope.loginForm.$valid) return;
        /** @type AuthenticationResult */
        const result = await LoginService.login();

        if (result.success) {
            onLoginSuccess();
        } else if (result.mfaRequired) {
            $scope.mfaRequired = true;
            $scope.mfaMode = result.mfaMode;
            // When first extending the form, make it pristine again so the validation doesnt show
            if (!result.mfaFailure) {
                resetValidation();
            }
        }
        return result.success;
    });

    // If the user is already logged in then accept the invite using the current account.
    // If the invite is not valid redirect the user to the installation overview.
    if ($stateParams.invite && ApiService.isLoggedIn()) {
        acceptInvite();
    }

    if ($stateParams.evt) {
        return ApiService.auth("/verify", {
            "verification_hash": $stateParams.evt
        }).then(function() {
            locale.ready('login').then(function () {
                vrmNotificationsService.add({
                    type: vrmNotificationType.SUCCESS,
                    message: locale.getString('login.email_verified')
                });
            });
            // If we received a token from the API, redirect us
            if (ApiService.isLoggedIn())
            {
                // Logout an login for the cases when the user is already logged in
                // Clears contexts and user specific data.
                $scope.$emit('logout');
                $scope.$emit('login');
                redirect();
            }
        });
    }

    if ($stateParams.emailHash) {
        return ApiService.auth('/verifychange', {
            'emailHash': $stateParams.emailHash
        }).then(function() {
            locale.ready('login').then(function() {
                vrmNotificationsService.add({
                    type: vrmNotificationType.SUCCESS,
                    message: locale.getString('login.email_changed'),
                });
            });
            if (ApiService.isLoggedIn()) {
                // Clear the context and user data
                localStorageService.remove("token");
                $scope.$emit('logout');
            }
            $state.go('login');
        });
    }

    if ($stateParams.returnParams && $stateParams.returnParams['demo-login']){
        if (!ApiService.isLoggedIn()) {
            $scope.demo();
        }
    }

    // Get the token as stateParams or URL params.
    const urlParams = $location.search();
    const tokenString = urlParams.token ? urlParams.token : $stateParams.token;

    $scope.loggingInUsingToken = false;
    // We can login by token in URL (used in VictronConnect, and Admin impersonation), but only if token is temporary (to prevent abuse)
    if (tokenString) {
        // Try to decode JWT
        try {
            const token = jwtHelper.decodeToken(tokenString);
            if (token) {
                // Logout an login for the cases when the user is already logged in
                // Clears contexts and user specific data.
                $scope.$emit('logout');
                $scope.loggingInUsingToken = true;
                localStorageService.set("token", tokenString);
                $scope.$emit('login');

                if ($stateParams.installationIdentifier) {
                    redirectToSiteByInstallationIdentifier($stateParams.installationIdentifier, $stateParams.device, $stateParams.productId);
                    return;
                }

                if (!urlParams.redirect || urlParams.redirect === "") {
                    redirect();
                } else {
                    $location.path(urlParams.redirect);
                }

            }
        } catch (e) {
            // Invalid token, show login screen
        }
    }

    // Redirect to the installation coresponding to a installation identifier passed as URL param.
    if (ApiService.isLoggedIn() && $stateParams.installationIdentifier) {
        redirectToSiteByInstallationIdentifier($stateParams.installationIdentifier, $stateParams.device, $stateParams.productId);
    }

    // Redirect to the installation overview if the user is logged in.
    if (ApiService.isLoggedIn() && !$stateParams.victronConnect) {
        redirect();
    }

    $scope.autoFocusMfa = function () {
        let smsTokenInput = document.getElementById('sms_token');
        smsTokenInput.focus();
    }
}
